import {formattedPropertyModel, utils} from '@wallaby-ui/core';
import {forEach, has, isNil, map} from 'lodash';

import {LIBRARY_NAME} from 'src/js/common/constants';

export const KEYWORD_DATA_CONTENT = 'content';
export const KEYWORD_DATA_CONTENT_LAST_CHANGE_TIMESTAMP = 'lastChangeTimestamp';
export const KEYWORD_DATA_CONTENT_LAST_CHANGE_AUTHOR = 'lastChangeAuthor';
export const KEYWORD_DATA_CURRENT_SCREEN_CONTENT = 'currentScreenContent';
export const KEYWORD_DATA_ITEMS = 'items';
export const KEYWORD_DATA_ERROR_TEXT = 'errorText';
export const KEYWORD_SCREEN_ID = 'screenId';

export const KEYWORD_PROP_CONTENT_LABEL = 'label';
export const KEYWORD_PROP_CONTENT_VALUE = '$value';
export const KEYWORD_PROP_CONTENT_VALIDATED = '$validated';
export const KEYWORD_PROP_CONTENT_TO_STRING = '$toString';

export const DATA_EMPTY = '[EMPTY]';

const logger = utils.LogUtils.getLogger(
  'AutoLayoutScreenDataHelpers',
  LIBRARY_NAME
);

export const ScreenDataHelpers = {
  /**
   * Creates empty data property used in the auto layout.
   * @param {ContentProperty|Object} contentProperty
   * @param {ContentSchema} rootContentDef
   * @returns {*} Empty object, when type of property is object, empty array,
   * when type of property is array and empty object containing null property '$value'
   */
  createEmptyDataProperty: (contentProperty, rootContentDef) => {
    let result = null;
    if (contentProperty.getRef()) {
      const componentType = rootContentDef
        .getComponent(contentProperty.getRef())
        .getType();
      result = componentType && componentType === 'array' ? [] : {};
    } else {
      switch (contentProperty.getType()) {
        case 'boolean':
        case 'date':
        case 'integer':
        case 'decimal':
        case 'number':
        case 'string':
          result = {[KEYWORD_PROP_CONTENT_VALUE]: null};
          break;
        default:
          logger.error('Unsupported type', contentProperty.getType());
      }

      // empty value of two-state checkbox (null is not allowed)
      if (
        contentProperty.getType() === 'boolean' &&
        contentProperty.getRequired()
      ) {
        result = {[KEYWORD_PROP_CONTENT_VALUE]: false};
      }
    }
    return result;
  },

  /**
   * Creates an empty content structure.
   *
   * @param {ContentSchema|Object} rootContentDef root content definition
   * @param {ContentComponent|Object} contentDef content definition
   * @returns {Object} data skeleton
   */
  createEmptyData: (rootContentDef, contentDef) => {
    const data = {};

    forEach(contentDef.getProperties(), (prop, name) => {
      data[name] = ScreenDataHelpers.createEmptyDataProperty(
        prop,
        rootContentDef
      );
    });

    return {[KEYWORD_DATA_CONTENT]: data};
  },

  /**
   * Creates an empty index structure.
   *
   * @returns {Object} index skeleton
   */
  createEmptyIndex: () => ({[KEYWORD_DATA_ITEMS]: []}),

  getItemValue: (data) => {
    let value = data;
    if (data && has(data, KEYWORD_PROP_CONTENT_VALUE)) {
      // if value has KEYWORD_PROP_CONTENT_VALUE, use it
      value = data[KEYWORD_PROP_CONTENT_VALUE];
    }
    if (value && has(value, KEYWORD_PROP_CONTENT_LABEL)) {
      // if value has label property, set value to it
      value = value[KEYWORD_PROP_CONTENT_LABEL];
    }
    if (isNil(value) || value === '') {
      value = DATA_EMPTY;
    }
    return value;
  },

  formatItemValue: (data, content, translationService, isArray = false) => {
    let value = data;
    if (data) {
      if (isArray) {
        value = map(content, (contentProperty, contentPropertyId) => {
          const propDefinition = new formattedPropertyModel.FormattedProperty({
            type: contentProperty.getType(),
          });
          const itemValue = ScreenDataHelpers.getItemValue(
            data[contentPropertyId]
          );
          return {
            property: contentPropertyId,
            value: formattedPropertyModel.FormattedProperty.formatValue(
              propDefinition,
              itemValue,
              translationService
            ),
          };
        });
      } else {
        const propDefinition = new formattedPropertyModel.FormattedProperty({
          type: content.getType(),
        });
        const itemValue = ScreenDataHelpers.getItemValue(data);
        value = formattedPropertyModel.FormattedProperty.formatValue(
          propDefinition,
          itemValue,
          translationService
        );
      }
    }
    return value;
  },

  getValueByContentProperty: (data, content, translationService, isArray) => {
    let value = data;
    if (isArray && data) {
      value = data.map((item) =>
        ScreenDataHelpers.formatItemValue(
          item,
          content,
          translationService,
          isArray
        )
      );
    } else {
      value = ScreenDataHelpers.formatItemValue(
        data,
        content,
        translationService
      );
    }
    return value;
  },

  getLastChangeUser(data) {
    let user = '';
    const screenData =
      data && data[KEYWORD_DATA_CURRENT_SCREEN_CONTENT]
        ? data[KEYWORD_DATA_CURRENT_SCREEN_CONTENT]
        : data;
    if (screenData) {
      if (screenData[KEYWORD_DATA_CONTENT_LAST_CHANGE_AUTHOR]) {
        user = screenData[KEYWORD_DATA_CONTENT_LAST_CHANGE_AUTHOR];
      } else if (
        screenData[KEYWORD_DATA_CONTENT] &&
        screenData[KEYWORD_DATA_CONTENT][
          KEYWORD_DATA_CONTENT_LAST_CHANGE_AUTHOR
        ]
      ) {
        user =
          screenData[KEYWORD_DATA_CONTENT][
            KEYWORD_DATA_CONTENT_LAST_CHANGE_AUTHOR
          ];
      }
    }

    return user;
  },

  getErrorText(data) {
    return data && data[KEYWORD_DATA_ERROR_TEXT]
      ? data[KEYWORD_DATA_ERROR_TEXT]
      : '';
  },
};

export default {ScreenDataHelpers};
